/**
 * Nueva campaña · Grupos (`/activa/segmentaciones/:id/grupos`, DISENO §6). Del equipo.
 *
 * - El reporte comercial en pantalla y las descargas: el Excel de públicos y el PDF.
 * - El nombre de la campaña se puede cambiar («Octubre · 2ª quincena»).
 * - Mientras falte el nombre comercial de algún grupo, el Excel y el PDF no se bajan (el nombre descriptivo nunca sale
 *   hacia afuera): arriba de todo, el equipo los pone. La vista técnica (lo que devolvió la corrida, tal cual) también
 *   es del equipo.
 * - «Siguiente: mensajes» lleva al paso 3 cuando los grupos ya tienen nombre (fase 3).
 * - La portada del reporte lleva el color, la letra y el logo de «Tu marca» (fase 3), como el PDF. El reporte espera a
 *   saberlos, para no cambiar de color en la cara del equipo; si no se pueden leer, sale con el negro y el nombre.
 */

import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, FileText, Loader2 } from "lucide-react";
import { brandingApi } from "../../branding/api";
import type { Branding } from "../../branding/types";
import { useRolActiva } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { llamar } from "../../wa-oficial/components/base/comun";
import { ErrorTarjeta, Esqueleto } from "../../wa-oficial/components/estadisticas/comun";
import { leerError, textoError } from "../../wa-oficial/logic/respuesta";
import { segmentacionesApi } from "../api";
import { NombresGrupos, type ResultadoNombres } from "../components/NombresGrupos";
import { Antetitulo, PasosCampana, btnPrimario, btnSecundario } from "../components/PasosCampana";
import { ReporteGrupos, type EstiloDelReporte } from "../components/ReporteGrupos";
import { rutaSegmentar, sinNombre } from "../logic/segmentar";
import { rutaMensajes } from "../../mensajes/logic/mensajes";
import type { Segmentacion } from "../types";

/** «Falta el nombre de «A»» o «Faltan los nombres de «A», «B» y «C»», con el nombre del motor: lo lee sólo el equipo. */
function faltanEnPalabras(gs: { nombreMotor?: string }[]): string {
  const q = gs.map((g) => (g.nombreMotor ? `«${g.nombreMotor}»` : "un grupo"));
  return q.length === 1 ? `Falta el nombre de ${q[0]}` : `Faltan los nombres de ${q.slice(0, -1).join(", ")} y ${q[q.length - 1]}`;
}

function NombreCampana({ s, alGuardar }: { s: Segmentacion; alGuardar: (x: Segmentacion) => void }) {
  const [valor, setValor] = useState(s.nombre);
  const [editando, setEditando] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => setValor(s.nombre), [s.nombre]);

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    const l = await llamar<Segmentacion>(() => segmentacionesApi.renombrar(s.id, valor.trim()));
    if (!l.ok) return setError(textoError(l));
    setError("");
    setEditando(false);
    alGuardar(l.data);
  };

  if (!editando) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold text-slate-900">{s.nombre}</p>
        <button type="button" onClick={() => setEditando(true)} className="min-h-[44px] px-2 text-sm font-semibold text-violet-700 hover:text-violet-900">
          Cambiar nombre
        </button>
      </div>
    );
  }
  return (
    <form onSubmit={guardar} className="flex flex-wrap items-center gap-2">
      <label htmlFor="nombre-campana" className="sr-only">
        Nombre de la campaña
      </label>
      <input
        id="nombre-campana"
        value={valor}
        maxLength={60}
        onChange={(e) => setValor(e.target.value)}
        aria-invalid={!!error || undefined}
        className="min-h-[44px] min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
      />
      <button type="submit" className={btnSecundario}>
        Guardar
      </button>
      {error && (
        <p role="alert" className="basis-full text-sm text-red-700">
          {error}
        </p>
      )}
    </form>
  );
}

export default function GruposPage() {
  const { id = "" } = useParams();
  const equipo = useRolActiva() === "equipo";
  const [s, setS] = useState<Segmentacion | null>(null);
  const [error, setError] = useState("");
  const [bajando, setBajando] = useState<"" | "excel" | "pdf">("");
  const [errorBajada, setErrorBajada] = useState("");
  // undefined = todavía no se sabe; null = no se pudo leer «Tu marca» (el reporte sale igual, sin ella)
  const [estilo, setEstilo] = useState<EstiloDelReporte | null | undefined>(undefined);

  const cargar = async () => {
    setError("");
    const l = await llamar<Segmentacion>(() => segmentacionesApi.ver(id));
    if (!l.ok) return setError(textoError(l));
    setS(l.data);
  };
  useEffect(() => {
    setS(null);
    void cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => {
    let vigente = true;
    void llamar<Branding>(() => brandingApi.ver()).then((l) => {
      if (vigente) setEstilo(l.ok ? { color: l.data.color, letra: l.data.letra, logo: l.data.logo } : null);
    });
    return () => {
      vigente = false;
    };
  }, []);

  const bajar = async (que: "excel" | "pdf") => {
    if (!s) return;
    setBajando(que);
    setErrorBajada("");
    try {
      await (que === "excel" ? segmentacionesApi.excel(s) : segmentacionesApi.pdf(s));
    } catch (e) {
      setErrorBajada(textoError(leerError(e)));
    } finally {
      setBajando("");
    }
  };

  const guardarNombres = async (nombres: Record<string, string>): Promise<ResultadoNombres> => {
    const l = await llamar<Segmentacion>(() => segmentacionesApi.nombrar(id, nombres));
    if (!l.ok) return { ok: false, error: textoError(l), grupoId: typeof l.extra.grupoId === "string" ? l.extra.grupoId : undefined };
    setS(l.data);
    return { ok: true };
  };

  const pendientes = s ? sinNombre(s) : [];
  const sePuedeBajar = !!s && s.estado === "borrador";
  // como el Excel y el PDF: Mensajes necesita los grupos con su nombre comercial
  const aMensajes = sePuedeBajar && pendientes.length === 0 ? rutaMensajes(id) : null;
  const nombres = new Map((s?.grupos ?? []).map((g) => [g.id, g.nombre]));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {s ? <NombreCampana s={s} alGuardar={setS} /> : <p className="text-sm font-semibold text-slate-900">Nueva campaña</p>}
      </div>
      <PasosCampana actual={1} rutas={[rutaSegmentar(id), null, aMensajes]} />

      {error && !s && <ErrorTarjeta texto={error} onReintentar={() => void cargar()} />}
      {!s && !error && <Esqueleto alto="h-96" etiqueta="Cargando los grupos" />}

      {s && (
        <>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="space-y-1.5">
              <Antetitulo>Paso 2 de 4</Antetitulo>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Tus grupos</h1>
            </div>
            {s.reporte && (
              <div className="flex flex-wrap gap-2">
                <button type="button" className={btnSecundario} disabled={!sePuedeBajar || !!bajando} onClick={() => void bajar("excel")}>
                  {bajando === "excel" ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Download size={16} aria-hidden="true" />}
                  Públicos (Excel)
                </button>
                <button type="button" className={btnSecundario} disabled={!sePuedeBajar || !!bajando} onClick={() => void bajar("pdf")}>
                  {bajando === "pdf" ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <FileText size={16} aria-hidden="true" />}
                  Reporte (PDF)
                </button>
              </div>
            )}
          </div>
          {errorBajada && <ErrorTarjeta texto={errorBajada} />}

          {s.estado === "procesando" || s.estado === "fallida" ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
              <p className="text-sm text-slate-600">{s.estado === "procesando" ? "Todavía estamos armando los grupos." : s.error?.texto || "No pudimos armar los grupos."}</p>
              <Link to={rutaSegmentar(s.id)} className={btnSecundario}>
                Volver a Segmentar
              </Link>
            </div>
          ) : (
            <>
              {equipo && (pendientes.length > 0 || (s.avisos?.length ?? 0) > 0) && (
                <NombresGrupos key={s.grupos.map((g) => `${g.id}:${g.nombre ?? ""}`).join("|")} grupos={s.grupos} guardar={guardarNombres} />
              )}
              {equipo && pendientes.length === 0 && !(s.avisos?.length ?? 0) && (
                <details className="rounded-2xl border border-slate-200 bg-white p-4 text-sm">
                  <summary className="cursor-pointer font-semibold text-slate-900">Cambiar los nombres de los grupos</summary>
                  <div className="mt-4">
                    <NombresGrupos key={s.grupos.map((g) => `${g.id}:${g.nombre ?? ""}`).join("|")} grupos={s.grupos} guardar={guardarNombres} />
                  </div>
                </details>
              )}
              {s.reporte &&
                (estilo === undefined ? (
                  <Esqueleto alto="h-96" etiqueta="Cargando el reporte" />
                ) : (
                  <ReporteGrupos reporte={s.reporte} nombres={nombres} estilo={estilo ?? undefined} />
                ))}
              {equipo && s.tecnica && (
                <details className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm" data-testid="vista-tecnica">
                  <summary className="cursor-pointer font-semibold text-slate-900">Vista técnica (sólo el equipo)</summary>
                  {!!s.avisos?.length && (
                    <ul className="mt-3 list-disc space-y-1 pl-5 text-amber-800">
                      {s.avisos.map((a) => (
                        <li key={a}>{a}</li>
                      ))}
                    </ul>
                  )}
                  <pre className="mt-3 max-h-[480px] overflow-auto rounded-xl bg-white p-3 text-xs text-slate-700">{JSON.stringify(s.tecnica, null, 2)}</pre>
                </details>
              )}
            </>
          )}

          <div className="flex flex-wrap justify-between gap-3 border-t border-slate-200 pt-4">
            <Link to={rutaSegmentar(s.id)} className={btnSecundario}>
              Atrás
            </Link>
            {aMensajes ? (
              <Link to={aMensajes} className={btnPrimario}>
                Siguiente: mensajes
              </Link>
            ) : (
              <div className="flex flex-wrap items-center justify-end gap-3">
                {/* por qué no se sigue, a la vista: el title sólo se veía pasando el mouse (Alan, 01/11/2026) */}
                {pendientes.length > 0 && (
                  <p id="por-que-no-mensajes" className="text-sm text-red-700">
                    {equipo ? (
                      <>
                        {faltanEnPalabras(pendientes)}: escribilo arriba y tocá «Guardar nombres».{" "}
                        <button type="button" className="font-semibold underline" onClick={() => document.getElementById(`nombre-grupo-${pendientes[0].id}`)?.focus()}>
                          Ir al que falta
                        </button>
                      </>
                    ) : (
                      "Falta que el equipo de Deenex les ponga nombre a los grupos."
                    )}
                  </p>
                )}
                <button type="button" className={btnPrimario} disabled aria-describedby={pendientes.length ? "por-que-no-mensajes" : undefined}>
                  Siguiente: mensajes
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
